import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getMockProductPricing, updateMockProductPricing } from '../data/mockData';
import { getProductPricing, updateProductPricing } from '../config/api';
import useIsMobile from '../hooks/useIsMobile';

const ProductPricing = () => {
  const isMobile = useIsMobile();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editedPriceSlabs, setEditedPriceSlabs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [mobileSearch, setMobileSearch] = useState('');

  useEffect(() => {
    // Check if user has superAdmin role
    const userData = JSON.parse(localStorage.getItem('userData_30jan') || '{}');
    if (userData.role !== 'superAdmin') {
      // Redirect to orders page if not superAdmin
      window.location.href = '/orders';
      return;
    }
    
    loadProductPricing();
  }, []);

  const loadProductPricing = async () => {
    setLoading(true);
    try {
      const data = await getProductPricing() || {};
      console.log(data);
      if(data.data) {
        setProducts(data.data);
      }
    } catch (error) {
      console.error('Error loading product pricing:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product.pid);
    setEditedPriceSlabs([...product.priceSlabs]);
  };

  const handleCancel = () => {
    setEditingProduct(null);
    setEditedPriceSlabs([]);
  };

  const handlePriceChange = (slabIndex, newPrice) => {
    const updatedSlabs = [...editedPriceSlabs];
    updatedSlabs[slabIndex] = {
      ...updatedSlabs[slabIndex],
      price: parseFloat(newPrice) || 0
    };
    setEditedPriceSlabs(updatedSlabs);
  };

  const handleSave = async (pid) => {
    setSaving(true);
    console.log(pid, editedPriceSlabs);
    try {
      let response = await updateProductPricing(pid, editedPriceSlabs);
      response = response.data;
      if(response.error) {
        alert(response.error);
        return;
      }
      // Update local state
      setProducts(prevProducts => 
        prevProducts.map(product => 
          product.pid === pid 
            ? { ...product, priceSlabs: editedPriceSlabs }
            : product
        )
      );
      
      setEditingProduct(null);
      setEditedPriceSlabs([]);
      alert('Pricing updated successfully!');
    } catch (error) {
      console.error('Error updating pricing:', error);
      alert('Failed to update pricing. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const formatPriceRange = (slab, index, slabs) => {
    const startQty = slab.qty;
    const endQty = slab.max || '∞';
    
    if (index === slabs.length - 1 && !slab.max) {
      return `${startQty}+ units`;
    }
    
    return `${startQty}-${endQty} units`;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const renderPriceSlabs = (product) => (
    <div className="price-slabs-container">
      {(editingProduct === product.pid ? editedPriceSlabs : product.priceSlabs).map((slab, index) => (
        <div key={index} className="price-slab-item">
          <div className="slab-range">
            {formatPriceRange(slab, index, product.priceSlabs)}
          </div>
          <div className="slab-price">
            {editingProduct === product.pid ? (
              <input
                type="number"
                inputMode="decimal"
                value={slab.price}
                onChange={(e) => handlePriceChange(index, e.target.value)}
                className="form-control price-input"
                min="0"
                step="0.01"
              />
            ) : (
              <span className="price-display">
                {formatPrice(slab.price)}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  const renderActions = (product) => (
    editingProduct === product.pid ? (
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
        <button
          onClick={() => handleSave(product.pid)}
          disabled={saving}
          className="btn btn-success"
          style={{ fontSize: '12px', padding: '6px 12px' }}
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button
          onClick={handleCancel}
          disabled={saving}
          className="btn btn-secondary"
          style={{ fontSize: '12px', padding: '6px 12px' }}
        >
          Cancel
        </button>
      </div>
    ) : (
      <button
        onClick={() => handleEdit(product)}
        className="btn btn-primary"
        style={{ fontSize: '12px', padding: '6px 12px' }}
      >
        Edit
      </button>
    )
  );

  if (loading) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center', padding: '60px' }}>
          <div style={{ fontSize: '18px', fontWeight: '600', color: '#495057' }}>
            Loading product pricing...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      {/* Page Header */}
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', ...(isMobile ? { flexWrap: 'wrap', gap: '12px' } : {}) }}>
          <div>
            <h1 className="page-title">Product Pricing Management</h1>
            <p className="page-subtitle">Manage price slabs for all products</p>
          </div>
          <Link to="/orders" className="btn btn-secondary">
            ← Back to Orders
          </Link>
        </div>
      </div>

      {/* Pricing Table */}
      <div className="card">
        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <input
              type="search"
              className="form-control"
              placeholder="Search product name or SKU…"
              value={mobileSearch}
              onChange={(e) => setMobileSearch(e.target.value)}
              autoCorrect="off"
              spellCheck={false}
            />
            {products.filter((product) => {
              const q = mobileSearch.trim().toLowerCase();
              return !q || `${product.productName} ${product.skuId} ${product.pid}`.toLowerCase().includes(q);
            }).map((product) => (
              <div key={product.pid} style={{ border: '1px solid #e9ecef', borderRadius: '8px', padding: '12px' }}>
                <div style={{ marginBottom: '8px' }}>
                  <div style={{ fontWeight: '600', fontSize: '15px' }}>{product.productName}</div>
                  <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>
                    <span style={{ fontWeight: '600', color: '#0d6efd' }}>#{product.pid}</span>
                    {' · '}
                    <span style={{ fontFamily: 'monospace' }}>{product.skuId}</span>
                  </div>
                </div>
                {renderPriceSlabs(product)}
                <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'flex-end' }}>
                  {renderActions(product)}
                </div>
              </div>
            ))}
          </div>
        ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="table" style={{ minWidth: '800px' }}>
            <thead>
              <tr>
                <th style={{ width: '80px' }}>ID</th>
                <th style={{ width: '150px' }}>SKU ID</th>
                <th style={{ width: '200px' }}>Product Name</th>
                <th style={{ minWidth: '400px' }}>Price Slabs</th>
                <th style={{ width: '120px', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => (
                <tr key={product.pid}>
                  <td style={{ fontWeight: '600', color: '#0d6efd' }}>
                    {product.pid}
                  </td>
                  <td style={{ fontFamily: 'monospace', fontSize: '13px' }}>
                    {product.skuId}
                  </td>
                  <td style={{ fontWeight: '500' }}>
                    {product.productName}
                  </td>
                  <td>
                    {renderPriceSlabs(product)}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {renderActions(product)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}

        {products.length === 0 && (
          <div style={{ 
            padding: '60px 40px', 
            textAlign: 'center',
            color: '#6c757d',
            fontSize: '16px'
          }}>
            <div style={{ fontWeight: '600', marginBottom: '8px' }}>No products found</div>
            <div>No product pricing data available</div>
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="card" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
        <h4 style={{ marginBottom: '15px', color: '#495057' }}>Instructions:</h4>
        <ul style={{ margin: '0', paddingLeft: '20px', color: '#6c757d' }}>
          <li>Click "Edit" to modify prices for a product's price slabs</li>
          <li>Quantity ranges (slabs) cannot be modified, only prices can be edited</li>
          <li>Enter new prices and click "Save" to update the pricing</li>
          <li>Click "Cancel" to discard changes without saving</li>
          <li>Price changes are applied immediately after saving</li>
        </ul>
      </div>
    </div>
  );
};

export default ProductPricing;